import React, { useEffect, useMemo, useState } from 'react';
import { CalendarIcon, EditIcon, PercentIcon, PlusIcon, SearchIcon, TagIcon, TrashIcon, ArchiveIcon, PowerOffIcon, CheckCircleIcon, RotateCcwIcon, XCircleIcon } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Select } from '../ui/Select';
import { Textarea } from '../ui/Textarea';
import { Modal } from '../ui/Modal';
import { Badge } from '../ui/Badge';
import { useData } from '../../contexts/DataContext';
import { useToast } from '../../contexts/ToastContext';
import { usePermissions } from '../../hooks/usePermissions';
import { useConfirm } from '../../contexts/ConfirmContext';
import { Offer } from '../../types';
import { formatCurrency, getToday } from '../../utils/helpers';
export function Offers() {
  const {
    offers,
    services,
    addOffer,
    updateOffer,
    deleteOffer,
    getService
  } = useData();
  const {
    canCreate,
    canEdit,
    canDelete,
    isReadOnly
  } = usePermissions();
  const {
    showToast
  } = useToast();
  const {
    confirm
  } = useConfirm();
  const readOnly = isReadOnly('offers');
  const [searchTerm, setSearchTerm] = useState('');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selectedOffer, setSelectedOffer] = useState<Offer | null>(null);
  const [offerToDelete, setOfferToDelete] = useState<Offer | null>(null);
  // View Mode State
  const [viewMode, setViewMode] = useState<'active' | 'archived' | 'deactivated'>('active');
  const [itemStatuses, setItemStatuses] = useState<Record<string, 'archived' | 'deactivated'>>({});
  useEffect(() => {
    try {
      const saved = localStorage.getItem('savidna-offers-status');
      if (saved) setItemStatuses(JSON.parse(saved));
    } catch (e) {
      console.error('Error loading offer statuses', e);
    }
  }, []);
  const updateItemStatus = (id: string, status: 'archived' | 'deactivated' | null) => {
    const newStatuses = {
      ...itemStatuses
    };
    if (status === null) {
      delete newStatuses[id];
    } else {
      newStatuses[id] = status;
    }
    setItemStatuses(newStatuses);
    localStorage.setItem('savidna-offers-status', JSON.stringify(newStatuses));
  };
  const [formData, setFormData] = useState({
    serviceId: '',
    discountPercent: '',
    validFrom: getToday(),
    validUntil: getToday(),
    status: 'active' as Offer['status'],
    notes: ''
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const serviceOptions = services.map(service => ({
    value: service.id,
    label: `${service.name} - ${formatCurrency(service.price || 0)}`
  }));
  const filteredOffers = useMemo(() => {
    const term = searchTerm.toLowerCase().trim();
    return offers.filter(offer => {
      const status = itemStatuses[offer.id];
      const matchesStatus = viewMode === 'active' ? !status : status === viewMode;
      if (!matchesStatus) return false;
      const service = getService(offer.serviceId);
      return !term || service?.name.toLowerCase().includes(term) || offer.notes?.toLowerCase().includes(term);
    }).sort((a, b) => b.validFrom.localeCompare(a.validFrom));
  }, [offers, searchTerm, getService, viewMode, itemStatuses]);
  const activeCount = offers.filter(o => !itemStatuses[o.id]).length;
  const archivedCount = offers.filter(o => itemStatuses[o.id] === 'archived').length;
  const deactivatedCount = offers.filter(o => itemStatuses[o.id] === 'deactivated').length;
  const openNewForm = () => {
    if (!canCreate('offers')) return;
    setSelectedOffer(null);
    setFormData({
      serviceId: '',
      discountPercent: '',
      validFrom: getToday(),
      validUntil: getToday(),
      status: 'active',
      notes: ''
    });
    setFormErrors({});
    setIsFormOpen(true);
  };
  const openEditForm = (offer: Offer) => {
    if (!canEdit('offers')) return;
    setSelectedOffer(offer);
    setFormData({
      serviceId: offer.serviceId,
      discountPercent: String(offer.discountPercent),
      validFrom: offer.validFrom,
      validUntil: offer.validUntil,
      status: offer.status,
      notes: offer.notes || ''
    });
    setFormErrors({});
    setIsFormOpen(true);
  };
  const getOfferStatus = (offer: Offer) => {
    const today = getToday();
    if (offer.status !== 'active') return 'Inactiva';
    if (offer.validFrom > today) return 'Programada';
    if (offer.validUntil < today) return 'Vencida';
    return 'Vigente';
  };
  const getDiscountedPrice = (offer: Offer) => {
    const service = getService(offer.serviceId);
    const price = service?.price || 0;
    return Math.max(0, price - price * (offer.discountPercent / 100));
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const errors: Record<string, string> = {};
    const discount = Number(formData.discountPercent);
    if (!formData.serviceId) errors.serviceId = 'Selecciona un servicio';
    if (Number.isNaN(discount) || discount <= 0 || discount > 100) {
      errors.discountPercent = 'El descuento debe ser mayor a 0 y menor o igual a 100';
    }
    if (!formData.validFrom) errors.validFrom = 'Selecciona la fecha inicial';
    if (!formData.validUntil) errors.validUntil = 'Selecciona la fecha final';
    if (formData.validFrom && formData.validUntil && formData.validFrom > formData.validUntil) {
      errors.validUntil = 'La fecha final no puede ser menor que la inicial';
    }
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    const dataToSave = {
      serviceId: formData.serviceId,
      discountPercent: discount,
      validFrom: formData.validFrom,
      validUntil: formData.validUntil,
      status: formData.status,
      notes: formData.notes
    };
    confirm({
      title: selectedOffer ? 'Guardar Cambios' : 'Crear Oferta',
      message: selectedOffer ? '¿Estás seguro de que deseas guardar los cambios de esta oferta?' : '¿Estás seguro de que deseas registrar esta nueva oferta?',
      confirmText: 'Guardar',
      onConfirm: async () => {
        try {
          if (selectedOffer) {
            await updateOffer(selectedOffer.id, dataToSave);
            showToast({
              type: 'success',
              title: 'Oferta actualizada',
              message: 'La oferta fue actualizada correctamente.'
            });
          } else {
            await addOffer(dataToSave);
            showToast({
              type: 'success',
              title: 'Oferta creada',
              message: 'La oferta fue registrada correctamente.'
            });
          }
          setIsFormOpen(false);
        } catch {
          showToast({
            type: 'error',
            title: 'Error al guardar',
            message: 'No se pudo guardar la oferta. Verifica la tabla en Supabase.'
          });
        }
      }
    });
  };
  const handleActionConfirm = (offer: Offer, type: 'archive' | 'deactivate' | 'delete') => {
    let title = '';
    let message = '';
    let confirmText = '';
    let variant: 'danger' | 'warning' | 'info' | 'primary' = 'primary';
    if (type === 'archive') {
      title = 'Archivar Oferta';
      message = '¿Estás seguro de que deseas archivar esta oferta? Podrás restaurarla más tarde.';
      confirmText = 'Archivar';
      variant = 'warning';
    } else if (type === 'deactivate') {
      title = 'Desactivar Oferta';
      message = '¿Estás seguro de que deseas desactivar esta oferta? No aparecerá en las listas activas.';
      confirmText = 'Desactivar';
      variant = 'warning';
    } else if (type === 'delete') {
      title = 'Eliminar Oferta';
      message = '¿Estás seguro de que deseas eliminar permanentemente esta oferta? Esta acción no se puede deshacer.';
      confirmText = 'Eliminar';
      variant = 'danger';
    }
    confirm({
      title,
      message,
      confirmText,
      variant,
      onConfirm: async () => {
        if (type === 'archive') {
          updateItemStatus(offer.id, 'archived');
          showToast({
            type: 'info',
            title: 'Oferta archivada',
            message: 'La oferta fue archivada.'
          });
        } else if (type === 'deactivate') {
          updateItemStatus(offer.id, 'deactivated');
          showToast({
            type: 'warning',
            title: 'Oferta desactivada',
            message: 'La oferta fue desactivada.'
          });
        } else if (type === 'delete') {
          await deleteOffer(offer.id);
          const newStatuses = {
            ...itemStatuses
          };
          delete newStatuses[offer.id];
          setItemStatuses(newStatuses);
          localStorage.setItem('savidna-offers-status', JSON.stringify(newStatuses));
          showToast({
            type: 'success',
            title: 'Oferta eliminada',
            message: 'La oferta fue eliminada correctamente.'
          });
        }
      }
    });
  };
  const handleRestore = (offer: Offer) => {
    updateItemStatus(offer.id, null);
  };
  return <div className="space-y-6" data-id="element-4064" data-mp="sy7gpu6oari6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4" data-id="element-4065" data-mp="ozrw1wy2dv7x">
        <div data-id="element-4066" data-mp="k0eyah5nn3qk">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-4067" data-mp="k0x1xyunck8i">
            Ofertas
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1" data-id="element-4068" data-mp="f00zzqtuv85q">
            Descuentos por servicio con rango de validez.
          </p>
        </div>
        {!readOnly && <Button onClick={openNewForm} leftIcon={<PlusIcon className="w-4 h-4" data-id="element-4070" data-mp="62gvm90p4vqw" />} data-id="element-4069" data-mp="60w28mq4bium">
          
            Nueva Oferta
          </Button>}
      </div>

      <Card data-id="element-4071" data-mp="20eevvlh12han">
        <CardHeader data-id="element-4072" data-mp="20dltewp3qdyb">
          <div className="flex flex-col gap-4" data-id="element-4073" data-mp="20fgjepf8cw99">
            <div className="flex items-center gap-2" data-id="element-4074" data-mp="1xpgutishzkyd">
              <TagIcon className="w-5 h-5 text-teal-600" data-id="element-4075" data-mp="1xo5trqqfsray" />
              <CardTitle data-id="element-4076" data-mp="1to5a9rohdpx1">Gestion de Ofertas</CardTitle>
            </div>

            <div className="flex flex-col md:flex-row gap-4 justify-between items-center" data-id="element-4077" data-mp="1tq80abpmvj1r">
              <div className="flex p-1 bg-gray-100 dark:bg-gray-800 rounded-xl w-full md:w-auto overflow-x-auto" data-id="element-4078" data-mp="27lk55bxi6pwe">
                {[{
                key: 'active',
                label: 'Activas',
                count: activeCount,
                icon: CheckCircleIcon
              }, {
                key: 'archived',
                label: 'Archivadas',
                count: archivedCount,
                icon: ArchiveIcon
              }, {
                key: 'deactivated',
                label: 'Desactivadas',
                count: deactivatedCount,
                icon: XCircleIcon
              }].map(tab => <button key={tab.key} onClick={() => setViewMode(tab.key as any)} className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${viewMode === tab.key ? 'bg-white dark:bg-gray-700 text-teal-600 dark:text-teal-400 shadow-sm' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'}`} data-id="element-4079" data-mp="32x06zt8m6bfl">
                  
                    <tab.icon className="w-4 h-4" data-id="element-4080" data-mp="3lnrcgjphdhop" />
                    {tab.label}
                    <span className="text-xs bg-gray-200 dark:bg-gray-600 px-1.5 py-0.5 rounded-full" data-id="element-4081" data-mp="3ql1bmr2o97pq">
                      {tab.count}
                    </span>
                  </button>)}
              </div>

              <div className="relative w-full md:w-80" data-id="element-4082" data-mp="3hnqzl3i15ejr">
                <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" data-id="element-4083" data-mp="2174shu07hbet" />
                <input value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Buscar por servicio..." className="w-full pl-9 pr-4 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all" data-id="element-4084" data-mp="2682ao6l7u5gb" />
                
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0" data-id="element-4085" data-mp="3p32mq28nw5kp">
          <div className="overflow-x-auto" data-id="element-4086" data-mp="3k5j9n5np9yuc">
            <table className="w-full" data-id="element-4087" data-mp="3k4hno655brxu">
              <thead data-id="element-4088" data-mp="3f6zo36d0qp2i">
                <tr className="bg-gray-50 dark:bg-gray-800/50 border-y border-gray-100 dark:border-gray-700" data-id="element-4089" data-mp="3f67prns1ed8j">
                  <th className="text-left py-3 px-6 text-xs font-semibold text-gray-500 uppercase" data-id="element-4090" data-mp="3b6fqy2sg3lgd">
                    Servicio
                  </th>
                  <th className="text-right py-3 px-6 text-xs font-semibold text-gray-500 uppercase" data-id="element-4091" data-mp="1dda369ousgw1">
                    Descuento
                  </th>
                  <th className="text-left py-3 px-6 text-xs font-semibold text-gray-500 uppercase" data-id="element-4092" data-mp="2rauvc7hgufgo">
                    Validez
                  </th>
                  <th className="text-left py-3 px-6 text-xs font-semibold text-gray-500 uppercase" data-id="element-4093" data-mp="311fpniq3wxts">
                    Estado
                  </th>
                  <th className="text-right py-3 px-6 text-xs font-semibold text-gray-500 uppercase" data-id="element-4094" data-mp="351rdt5hohga0">
                    Precio Oferta
                  </th>
                  {!readOnly && <th className="text-right py-3 px-6 text-xs font-semibold text-gray-500 uppercase" data-id="element-4095" data-mp="2dco61fql1nsm">
                      Acciones
                    </th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800" data-id="element-4096" data-mp="2mduq1ysfxgqm">
                {filteredOffers.length === 0 ? <tr data-id="element-4097" data-mp="3suzsmu6r1c16">
                    <td colSpan={readOnly ? 5 : 6} className="py-12 text-center text-gray-500 dark:text-gray-400" data-id="element-4098" data-mp="1lsvlf9gbwo1">
                    
                      No hay ofertas registradas
                    </td>
                  </tr> : filteredOffers.map(offer => {
                const service = getService(offer.serviceId);
                const status = getOfferStatus(offer);
                return <tr key={offer.id} className="group hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors" data-id="element-4099" data-mp="56mbclrdfoqp">
                      
                        <td className="py-4 px-6" data-id="element-4100" data-mp="e2tww2ilrhha">
                          <p className="text-sm font-semibold text-gray-900 dark:text-white" data-id="element-4101" data-mp="su3zm7bfg4ox">
                            {service?.name || 'Servicio eliminado'}
                          </p>
                          {offer.notes && <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 line-clamp-1" data-id="element-4102" data-mp="11usbowh9fuz6">
                              {offer.notes}
                            </p>}
                        </td>
                        <td className="py-4 px-6 text-right text-sm font-bold text-teal-600 dark:text-teal-400" data-id="element-4103" data-mp="1lgal6grlbh0z">
                          {offer.discountPercent}%
                        </td>
                        <td className="py-4 px-6 text-sm text-gray-600 dark:text-gray-300 tabular-nums" data-id="element-4104" data-mp="1bi4ms94zst0v">
                          <span className="inline-flex items-center gap-1.5" data-id="element-4105" data-mp="xqq4k4iwsvuv">
                            <CalendarIcon className="w-3.5 h-3.5 text-gray-400" data-id="element-4106" data-mp="xon4ero1lmcf" />
                            {offer.validFrom} al {offer.validUntil}
                          </span>
                        </td>
                        <td className="py-4 px-6" data-id="element-4107" data-mp="nqpjad1hc5o1">
                          <Badge variant={status === 'Vigente' ? 'success' : status === 'Programada' ? 'info' : 'default'} data-id="element-4108" data-mp="ssetxrksihbl">
                          
                            {status}
                          </Badge>
                        </td>
                        <td className="py-4 px-6 text-right text-sm" data-id="element-4109" data-mp="2gnb9lld0u6v0">
                          <span className="font-semibold text-gray-900 dark:text-white" data-id="element-4110" data-mp="2br49wab7xrli">
                            {formatCurrency(getDiscountedPrice(offer))}
                          </span>
                          {service && <span className="block text-xs text-gray-400 line-through" data-id="element-4111" data-mp="34cw1wuv2eggy">
                              {formatCurrency(service.price)}
                            </span>}
                        </td>
                        {!readOnly && <td className="py-4 px-6" data-id="element-4112" data-mp="2qepek8nqryvj">
                            <div className="flex justify-end gap-1 opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity" data-id="element-4113" data-mp="1jyvfdbyyv9bk">
                              {viewMode === 'active' ? <>
                                  {canEdit('offers') && <button type="button" onClick={() => openEditForm(offer)} className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors" title="Editar" data-id="element-4114" data-mp="3qlku9hwv1gb5">
                              
                                      <EditIcon className="w-4 h-4" data-id="element-4115" data-mp="dalrg357so5h" />
                                    </button>}
                                  {canDelete('offers') && <>
                                      <button type="button" onClick={() => handleActionConfirm(offer, 'archive')} className="p-2 text-gray-400 hover:text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-900/20 rounded-lg transition-colors" title="Archivar" data-id="element-4116" data-mp="r80nhqpz3u6f">
                                
                                        <ArchiveIcon className="w-4 h-4" data-id="element-4117" data-mp="2ivp3ul10zwj5" />
                                      </button>
                                      <button type="button" onClick={() => handleActionConfirm(offer, 'deactivate')} className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors" title="Desactivar" data-id="element-4118" data-mp="2etlhv2gqd9jm">
                                
                                        <PowerOffIcon className="w-4 h-4" data-id="element-4119" data-mp="36ljgb1hfnj2v" />
                                      </button>
                                    </>}
                                </> : canDelete('offers') && <button type="button" onClick={() => handleRestore(offer)} className="p-2 text-gray-400 hover:text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg transition-colors" title="Restaurar" data-id="element-4120" data-mp="3j1skg9hc8y3q">
                            
                                    <RotateCcwIcon className="w-4 h-4" data-id="element-4121" data-mp="6lyqac7qffgo" />
                                  </button>}
                            </div>
                          </td>}
                      </tr>;
              })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <Modal isOpen={isFormOpen} onClose={() => setIsFormOpen(false)} title={selectedOffer ? 'Editar Oferta' : 'Nueva Oferta'} size="md" icon={<PercentIcon className="w-5 h-5" data-id="element-4123" data-mp="2kzfoqliqzpu5" />} accentColor="emerald" data-id="element-4122" data-mp="pd9cig2ij428">
        
        <form onSubmit={handleSubmit} className="space-y-4" data-id="element-4124" data-mp="2l1ied4fzngyc">
          <Select label="Servicio" options={serviceOptions} value={formData.serviceId} onChange={value => setFormData({
          ...formData,
          serviceId: value
        })} error={formErrors.serviceId} searchable placeholder="Seleccionar servicio..." data-id="element-4125" data-mp="277rd0jcev3dp" />
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-4126" data-mp="1oaq2kj038b2d">
            <Input type="number" label="Descuento (%)" value={formData.discountPercent} onChange={e => setFormData({
            ...formData,
            discountPercent: e.target.value
          })} error={formErrors.discountPercent} min={1} max={100} step="0.01" data-id="element-4127" data-mp="1t7zrzynh8mq7" />
            
            <Select label="Estado" options={[{
            value: 'active',
            label: 'Activa'
          }, {
            value: 'inactive',
            label: 'Inactiva'
          }]} value={formData.status} onChange={value => setFormData({
            ...formData,
            status: value as Offer['status']
          })} data-id="element-4128" data-mp="3ssxpq58fbzyg" />
            
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-4129" data-mp="t9wqymvmudi3">
            <Input type="date" label="Valida desde" value={formData.validFrom} onChange={e => setFormData({
            ...formData,
            validFrom: e.target.value
          })} error={formErrors.validFrom} data-id="element-4130" data-mp="pb5t2djcertx" />
            
            <Input type="date" label="Valida hasta" value={formData.validUntil} onChange={e => setFormData({
            ...formData,
            validUntil: e.target.value
          })} error={formErrors.validUntil} data-id="element-4131" data-mp="z0hmsak5uhhp" />
            
          </div>
          <Textarea label="Notas" value={formData.notes} onChange={e => setFormData({
          ...formData,
          notes: e.target.value
        })} rows={3} placeholder="Opcional..." data-id="element-4132" data-mp="1czpkt04l7jkb" />
          
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-100 dark:border-gray-800 modal-actions" data-id="element-4133" data-mp="1roloym3iwcfn">
            <Button variant="outline" type="button" onClick={() => setIsFormOpen(false)} data-id="element-4134" data-mp="1wm57hy797bde">
              
              Cancelar
            </Button>
            <Button type="submit" data-id="element-4135" data-mp="3l96uyy1dwto6">
              {selectedOffer ? 'Guardar Cambios' : 'Crear Oferta'}
            </Button>
          </div>
        </form>
      </Modal>
    </div>;
}